import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";
import { captureVisit, recordView, installFlushHooks } from "@/utils/marketingTracker";
import { SSO_ONLY, ssoLogin } from "@/mainconfig";
import { hasNewBuild, isChunkError, reloadTo } from "@/utils/appVersion";

// Define your routes
const routes = [
    {
        path: "/",
        component: () => import("@/components/layout/MainLayout.vue"),
        children: [
            {
                path: "/",
                name: "Home",
                routeLabel: "routes.home", // Label untuk i18n
                component: () => import("@/views/HomePage.vue"),
            },
            {
                path: "/properties",
                name: "Properties",
                routeLabel: "routes.properties", // Label untuk i18n
                component: () => import("@/views/PropertiesPage.vue"),
                beforeEnter: (to, from, next) => {
                    const payload =
                        store.getters["properties/getSearchPayload"];
                    if (!payload) {
                        // console.error(
                        //     "Search payload is missing. Redirecting to home."
                        // );
                        next();
                    } else {
                        next();
                    }
                },
            },
            {
                path: "/:slug",
                name: "RoomDetail",
                routeLabel: "routes.roomDetail",
                component: () => import("@/views/Detail/RoomDetail.vue"),
                // props: true, // Pass route params as props
            },
            {
                path: "properties/:slug/confirmation",
                name: "Confirm Reservation",
                routeLabel: "routes.confirmReservation",
                component: () => import("@/views/ConfirmReservation.vue"),
            },
            {
                // Satu komponen dengan /booking/:bookingCode — dulu dua salinan yang isinya
                // perlahan berbeda (link exclusive kehilangan kartu bayar reschedule, unduh
                // invoice, dan permintaan pembatalan). Slug diterjemahkan jadi kode booking di
                // dalam komponen.
                path: "/link/exclusive/:slug",
                name: "ExclusiveRoomDetailLink",
                routeLabel: "routes.exclusiveRoomDetail",
                component: () =>
                    import("@/views/Booking/BookingDetail.vue"),
            },
            {
                // Bentuk URL lama (pra-v1.7). Tidak punya halaman sendiri lagi — dulu menunjuk
                // salinan kedua yang isinya sudah tertinggal dari halaman booking detail.
                // Dijadikan redirect, bukan dihapus: URL bentuk ini sudah terlanjur tersebar.
                path: "/exclusive/:slug",
                redirect: (to) => `/link/exclusive/${to.params.slug}`,
            },
            {
                path: "/payment",
                name: "Payment",
                routeLabel: "routes.payment",
                component: () => import("@/views/Payment.vue"),
            },
            {
                path: "/cart",
                name: "Cart",
                routeLabel: "routes.cart",
                component: () => import("@/views/Cart.vue"),
            },
            {
                path: "/properties/:slug",
                name: "PropertyDetail",
                routeLabel: "routes.propertyDetail",
                component: () => import("@/views/PropertyPage.vue"),
            },
            {
                path: "/pay",
                name: "Pay",
                routeLabel: "routes.pay",
                component: () => import("@/views/Pay.vue"),
            },
            {
                // CH-W08 - shared cancellation page (login mode); token mode di /link/cancellation/:token
                path: "/booking/:bookingCode/cancel",
                name: "Booking Cancel",
                routeLabel: "routes.bookingDetail",
                component: () =>
                    import("@/views/Booking/CancellationLink.vue"),
                props: true,
            },
            {
                path: "/booking/:bookingCode",
                name: "Booking Detail",
                routeLabel: "routes.bookingDetail",
                component: () =>
                    import("@/views/Booking/BookingDetail.vue"),
                props: true,
            },
            {
                path: "/link/cancellation/:token",
                name: "Cancellation Link",
                routeLabel: "routes.bookingDetail",
                component: () =>
                    import("@/views/Booking/CancellationLink.vue"),
            },
            {
                path: "/accommodation",
                name: "Accommodation",
                routeLabel: "routes.accommodation",
                component: () => import("@/views/Accommodation.vue"),
            },
            {
                path: "/complete-reservation",
                name: "Complete your Reservations",
                routeLabel: "routes.completeReservation",
                component: () => import("@/views/CompleteReservation.vue"),
            },
            {
                path: "/dashboard",
                name: "Dashboard", // Tambahkan nama ini
                routeLabel: "routes.dashboard",
                component: () => import("@/views/Dashboard.vue"),
                redirect: "/dashboard/ongoing", // <— gunakan redirect, bukan "direct"
                children: [
                    {
                        path: "profile",
                        name: "Profile",
                        routeLabel: "routes.profile",
                        component: () => import("@/views/Profile.vue"),
                        menu: true,
                        icon: "mdi:plus",
                        beforeEnter: (to, from, next) => {
                            const isGuest = store.getters["user/isGuest"]; // Ambil status isGuest dari store
                            if (isGuest) {
                                console.warn(
                                    "Guest users cannot access the Profile page."
                                );
                                return next(false); // Batalkan navigasi
                            }
                            next(); // Lanjutkan navigasi jika bukan guest
                        },
                    },
                    {
                        path: "ongoing",
                        name: "Ongoing Order",
                        routeLabel: "routes.ongoingOrder",
                        component: () => import("@/views/OnGoingOrder.vue"),
                        menu: true,
                        icon: "mdi:plus",
                    },
                    {
                        path: "orderhistory",
                        name: "Order History",
                        routeLabel: "routes.orderHistory",
                        component: () => import("@/views/BookingHistory.vue"),
                        menu: true,
                        icon: "mdi:plus",
                    },
                    {
                        // CH-W54 — Voucher Saya
                        path: "vouchers",
                        name: "My Vouchers",
                        routeLabel: "routes.myVouchers",
                        component: () => import("@/views/MyVouchers.vue"),
                        menu: true,
                        icon: "mdi:ticket-percent-outline",
                        beforeEnter: (to, from, next) => {
                            if (store.getters["user/isGuest"]) return next(false);
                            next();
                        },
                    },
                    {
                        path: "notifications",
                        name: "Customer Notification Center",
                        routeLabel: "routes.notifications",
                        component: () => import("@/views/NotificationCenter.vue"),
                        menu: true,
                        icon: "mdi:bell",
                        beforeEnter: (to, from, next) => {
                            if (store.getters["user/isGuest"]) return next(false);
                            next();
                        },
                    },
                    {
                        path: "notifications/:id",
                        name: "Customer Notification Detail",
                        routeLabel: "routes.notificationDetail",
                        component: () => import("@/views/NotificationDetail.vue"),
                        beforeEnter: (to, from, next) => {
                            if (store.getters["user/isGuest"]) return next(false);
                            next();
                        },
                    },
                ],
            },
        ],
    },
    {
        path: "/confirm-reservation",
        name: "Confirm Reservations",
        routeLabel: "routes.confirmReservation",
        component: () => import("@/views/ConfirmReservation.vue"),
    },
    {
        path: "/login",
        name: "Login",
        routeLabel: "routes.login",
        component: () => import("@/views/Login.vue"),
        beforeEnter: (to, from, next) => {
            const isAuthenticated = store.getters["user/isAuthenticated"];
            const isGuest = store.getters["user/isGuest"]; // Ambil status isGuest dari store

            if (isAuthenticated && !isGuest) {
                console.warn(
                    "User is already authenticated. Redirecting to dashboard."
                );
                return next("/dashboard"); // Redirect ke dashboard hanya jika bukan guest
            }

            // CH-W37 — SSO-only: langsung ke SSO, tak ada halaman login lokal.
            if (SSO_ONLY) {
                ssoLogin(to.query.redirect || "/dashboard");
                return next(false);
            }

            next(); // Lanjutkan navigasi jika belum login atau guest
        },
    },
    {
        path: "/auth/google/callback",
        name: "GoogleAuthCallback",
        component: () => import("@/views/GoogleAuthCallback.vue"),
    },
    {
        // CH-W37 — callback SSO: backend redirect ke sini dgn #token=... (atau ?error=...).
        path: "/auth/callback",
        name: "SsoCallback",
        component: () => import("@/views/SsoCallback.vue"),
    },
    {
        path: "/register",
        name: "Register",
        routeLabel: "routes.register",
        component: () => import("@/views/Register.vue"),
        beforeEnter: (to, from, next) => {
            const isAuthenticated = store.getters["user/isAuthenticated"];
            const isGuest = store.getters["user/isGuest"];
            if (isAuthenticated && !isGuest) return next("/dashboard");
            // CH-W37 — SSO-only: register juga lewat SSO.
            if (SSO_ONLY) {
                ssoLogin("/dashboard");
                return next(false);
            }
            next();
        },
    },
    {
        path: "/404",
        name: "404",
        routeLabel: "routes.notFound",
        component: () => import("@/views/NotFound.vue"),
    },
    {
        path: "/:catchAll(.*)", // Catch-all route for 404 errors
        redirect: "/404",
    },
];

// Create the router instance
const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        // back/forward
        if (savedPosition) return savedPosition;

        // kalau ada anchor (#id)
        if (to.hash) {
            return {
                el: to.hash,
                top: 0,
                behavior: "smooth",
            };
        }

        // ⛔️ jika navigasi masih di dalam dashboard (sidebar switch), jangan scroll ke atas
        if (
            to.path.startsWith("/dashboard") &&
            from.path.startsWith("/dashboard")
        ) {
            return false; // tetap di posisi sekarang
        }

        // default behavior: scroll ke atas
        return { left: 0, top: 0, behavior: "smooth" };
    },
});

let isFirstLoad = true;

// Example navigation guard (optional)
router.beforeEach(async (to, from, next) => {
    if (isFirstLoad && !from.name) {
        to.meta.isDirect = true;
    } else {
        to.meta.isDirect = false;
    }

    isFirstLoad = false;

    // CH-W39: tangkap query param marketing saat landing (non-blocking, fire-and-forget).
    captureVisit(to);

    const isAuthenticated = store.getters["user/isAuthenticated"]; // Ambil status autentikasi dari store
    const isFromCart = store.getters["cart/isFromCart"];

    // Jika berpindah dari Cart ke MapArea, set `isFromCart` ke true
    if (from.name === "Cart" && to.name === "Area") {
        store.commit("cart/setIsFromCart", true);
        console.log("Navigated from Cart to MapArea. isFromCart set to true.");
        return next();
    }

    // Jika keluar dari halaman mana pun dan `isFromCart` masih true, reset ke false
    if (isFromCart && to.name !== "Cart") {
        store.commit("cart/setIsFromCart", false);
        console.log("isFromCart reset to false as user left cart context.");
    }

    if (
        to.matched.some((record) => record.meta.requiresAuth) &&
        !isAuthenticated
    ) {
        // CH-W37 — SSO-only: route ber-auth yang belum login langsung ke SSO.
        if (SSO_ONLY) {
            ssoLogin(to.fullPath);
            return next(false);
        }
        // Jika route memerlukan autentikasi dan user belum login
        next({ name: "Login", query: { redirect: to.fullPath } }); // Redirect ke login dengan query string
    } else {
        next(); // Lanjutkan ke route berikutnya
    }
});

// CH-W39: rekam pageview tiap perpindahan route (batch, dikirim via sendBeacon).
router.afterEach((to) => {
    recordView(to);
});
installFlushHooks();

// Deploy menghapus chunk lama, sedangkan tab yang sudah terbuka masih memegang nama lama.
// Tanpa ini, klik ke halaman yang belum pernah dibuka gagal diam-diam: tak terjadi apa-apa.
router.onError((err, to) => {
    if (isChunkError(err?.message)) reloadTo(to?.fullPath || window.location.href);
});

// Build baru terdeteksi: muat ulang DITUNDA sampai user berpindah halaman, karena saat itu
// isi layar memang sedang ditinggalkan. Jadi tak perlu timer idle maupun tebakan "form ini
// terisi atau tidak" yang pasti meleset di suatu tempat.
router.beforeEach((to, from, next) => {
    if (hasNewBuild() && from.matched.length && reloadTo(to.fullPath)) return next(false);
    next();
});

export default router;
